<script setup lang="ts">
// i18n
import { HouseSessionEventStatus } from "@/enums/houseEventEnums";
import { useHouseSession } from "@/features/dream-home/store/houseSession";
import {
  HouseEvent,
  HouseEventEnding,
  MateHouseSessionEvent,
} from "@/types/entity";
import gsap from "gsap";
import { ref } from "vue";

defineProps<{
  event: HouseEvent;
  index: number;
}>();

const emit = defineEmits<{
  (e: "view-ending", ending: HouseEventEnding): void;
  (e: "start-event", sessionEvent: MateHouseSessionEvent): void;
}>();

const toggle = ref(false);

const getEventItemProgress = (event: HouseEvent) => {
  const total = event.endingzz.length;
  const completed = event.endingzz.reduce((acc, ending) => {
    if (isEndingFinished(ending)) {
      return acc + 1;
    }
    return acc;
  }, 0);
  return Math.ceil((completed / total) * 100);
};

const isEndingFinished = (ending: HouseEventEnding) => {
  return ending.completedHouseSessionEventzz.some((e) => e.isPlayed);
};

const isEventHasEndingGeneratedButNotPlayed = (event: HouseEvent) => {
  return event.endingzz.some((ending) => isEndingGeneratedButNotPlayed(ending));
};

const isEndingGeneratedButNotPlayed = (ending: HouseEventEnding) => {
  if (!ending.completedHouseSessionEventzz) return false;
  return ending.completedHouseSessionEventzz.some(
    (e) => e.status === HouseSessionEventStatus.Completed && !e.isPlayed
  );
};

const onLeave = (el: Element, done: () => void) => {
  gsap.timeline({ onComplete: done }).to(el, { height: 0, duration: 0.1 }, 0);
};

const onEnter = (el: Element, done: () => void) => {
  gsap
    .timeline({ onComplete: done })
    .to(el, { height: "auto", duration: 0.1 }, 0);
};
const houseStore = useHouseSession();
const onClickStart = (ending: HouseEventEnding) => {
  const targetSessionEvent = Object.values(houseStore.sessionEventMap)
    .filter((e) => e.endingId === ending.id)
    .find(
      (e) =>
        e.status === HouseSessionEventStatus.Completed && e.isPlayed === false
    );
  if (!targetSessionEvent) return;
  emit("start-event", targetSessionEvent);
};
</script>

<template>
  <div
    class="flex flex-col p-2 rounded cursor-pointer bg-base-100 gap-2 event-list-item overflow-hidden"
    @click="toggle = !toggle"
  >
    <div class="flex items-center justify-between">
      <div class="flex items-center">
        <div>{{ index + 1 }}.</div>
        <div>{{ event.title || "??" }}</div>
      </div>
      <div class="text-base-content/50 flex items-center gap-2">
        <div
          v-if="isEventHasEndingGeneratedButNotPlayed(event)"
          class="badge badge-warning badge-outline rounded"
        >
          事件已就绪
        </div>
        <div class="flex items-center gap-0.5">
          完成度: {{ getEventItemProgress(event) }}%
          <i
            class="iconify mingcute--right-small-fill"
            :class="{ 'rotate-90': toggle }"
          />
        </div>
      </div>
    </div>
    <Transition @leave="onLeave" @enter="onEnter" name="collapse">
      <div class="flex flex-col gap-1" v-show="toggle">
        <div
          v-for="(ending, idx) in event.endingzz"
          :key="ending.id"
          class="flex justify-between items-center"
        >
          <div class="flex items-center gap-1">
            <i class="iconify ph--dot-duotone" />
            <div>
              结局{{ idx + 1 }}:
              {{ isEndingFinished(ending) ? ending.title : "???" }}
            </div>
          </div>
          <div class="flex items-center gap-2">
            <button
              v-if="isEndingGeneratedButNotPlayed(ending)"
              class="btn btn-primary btn-xs rounded"
              @click.prevent="onClickStart(ending)"
            >
              开始
            </button>
            <button
              v-if="ending.completedHouseSessionEventzz.length > 0"
              class="cursor-pointer"
              @click.prevent="emit('view-ending', ending)"
            >
              查看详情
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.collapse-enter-from,
.collapse-leave-to {
  height: 0;
}
</style>
